/**
 * Purchase Orders page — the dedicated home for PO management.
 *
 * Hosts the same shared PO manager used by the Inventory Forecast and
 * Cashflow dialogs, rendered inline (standalone) instead of as a modal.
 * POs entered here flow into the inventory forecast and cashflow
 * automatically — all three surfaces read the same PO records.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import CashflowService from "services/CashflowService";
import InventoryService, { CatalogSuggestions } from "services/InventoryService";
import { PurchaseOrder, WRO, WroAllocationsRequest, WroLineAllocationsRequest, WroLinkRequest } from "models/InventoryModel";
import { Supplier } from "models/CashflowModel";
import { usePOManagement } from "shared/hooks/usePOManagement";
import { POManagementDialog, DeletePOConfirmation } from "shared/components/dialogs";
import { downloadBlob } from "shared/utils/downloadBlob";
import { persistPO } from "shared/utils/persistPO";
import NewProductDialog from "shared/components/NewProductDialog";
import WroClaimsDialog, { claimNeedsFollowUp } from "shared/components/dialogs/WroClaimsDialog";
import SavePricesToSkuMasterDialog, { PriceChange, ProductNumberChange, findPriceChanges, findProductNumberChanges } from "shared/components/dialogs/SavePricesToSkuMasterDialog";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

export default function PurchaseOrdersPage(): JSX.Element {
  const inventoryService = useMemo(() => new InventoryService(), []);
  const cashflowService = useMemo(() => new CashflowService(), []);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryOptions, setInventoryOptions] = useState<Array<{ id: string; name: string | null; skus: string[] }>>([]);
  const [consolidatedPOs, setConsolidatedPOs] = useState<PurchaseOrder[]>([]);
  const [wros, setWros] = useState<WRO[]>([]);
  // New shortfalls with no claim yet + open claims gone quiet (need follow-up)
  const [shortfallsNeedingAttention, setShortfallsNeedingAttention] = useState(0);

  const fetchShortfallAttention = useCallback(() => {
    inventoryService.getWroShortfalls().then(result => {
      if (result) {
        setShortfallsNeedingAttention(result.shortfalls.filter(s =>
          !s.claim || (s.claim.status !== "dismissed" && claimNeedsFollowUp(s.claim))
        ).length);
      }
    });
  }, [inventoryService]);
  const [isClaimsDialogOpen, setIsClaimsDialogOpen] = useState(false);
  // Offered after a save when line prices differ from the SKU master
  const [priceChangesToOffer, setPriceChangesToOffer] = useState<{ poNumber: string; changes: PriceChange[]; productNumberChanges: ProductNumberChange[] } | null>(null);
  const [catalogSuggestions, setCatalogSuggestions] = useState<CatalogSuggestions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingPaidStatus, setIsUpdatingPaidStatus] = useState(false);
  const [generatingPdfPoNumber, setGeneratingPdfPoNumber] = useState<string | null>(null);
  const [isNewProductDialogOpen, setIsNewProductDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Products created here, held outside the fetched list so a refetch of
  // /current-inventory (which does not know them yet) cannot drop them. A ref,
  // not state, so fetchPOsAndInventory keeps a stable identity.
  const createdProductsRef = useRef<Array<{ id: string; name: string | null; skus: string[] }>>([]);

  // ── Data fetching ──
  const fetchPOsAndInventory = useCallback(async () => {
    const [invResult, posResult, wroResult] = await Promise.all([
      inventoryService.getCurrentInventory(),
      inventoryService.getIncomingPOs(),
      inventoryService.getWROs(),
    ]);

    if (wroResult) setWros((wroResult as any).wros || []);

    if (invResult) {
      const records = Array.isArray(invResult) ? invResult : Object.values(invResult);
      const optionsMap = new Map<string, { id: string; name: string | null; skus: string[] }>();
      for (const item of records as any[]) {
        if (item?.inventory_id && !optionsMap.has(item.inventory_id)) {
          optionsMap.set(item.inventory_id, {
            id: item.inventory_id,
            name: item.inventory_name || null,
            skus: item.skus || [],
          });
        }
      }
      // A product created on this page is not in /current-inventory until the
      // next ShipBob products sync, so re-add it after every refetch.
      for (const created of createdProductsRef.current) {
        if (!optionsMap.has(created.id)) optionsMap.set(created.id, created);
      }
      setInventoryOptions(Array.from(optionsMap.values()));
    }

    if (posResult) {
      const allPOs = (posResult as any).pos || [];
      setConsolidatedPOs(allPOs.map((po: any) => ({
        ...po,
        description: po.description || "",
        // Taken whole rather than field by field. The server's toLineItem already
        // returns exactly the shape POManagementDialog reads -- it needs the WRO
        // fields to draw the "linked to ShipBob WRO" badge and its stowed-quantity
        // breakdown -- and a hand-listed mapper here has silently dropped them
        // twice: once by omitting the pricing fields, once by keeping `wroId`
        // after #187 renamed it `wroIds`. inventoryName is attached in namedPOs.
        lineItems: (po.lineItems || []).map((li: any) => ({ ...li })),
      })));
    }

    if (!invResult || !posResult) {
      setErrorMessage("Failed to load purchase orders");
    }
    setIsLoading(false);
  }, [inventoryService]);

  useEffect(() => {
    cashflowService.getSuppliers().then(result => {
      if (result) setSuppliers(result.suppliers);
    });
    inventoryService.getCatalogPrices().then(result => {
      if (result) setCatalogSuggestions(result);
    });
    fetchShortfallAttention();
    fetchPOsAndInventory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach names to line items once options are loaded
  const namedPOs = useMemo(() => {
    const nameById = new Map(inventoryOptions.map(opt => [opt.id, opt.name]));
    return consolidatedPOs.map(po => ({
      ...po,
      lineItems: po.lineItems.map(li => ({
        ...li,
        inventoryName: nameById.get(li.inventoryId) || li.inventoryName || undefined,
      })),
    }));
  }, [consolidatedPOs, inventoryOptions]);

  // Every known SKU — powers the new-product SKU builder's vocab + uniqueness check
  const existingSkus = useMemo(() => {
    const skus = new Set<string>();
    for (const opt of inventoryOptions) {
      for (const sku of opt.skus) skus.add(sku);
    }
    return Array.from(skus);
  }, [inventoryOptions]);

  const handleLinkWRO = async (poNumber: string, link: WroLinkRequest) => {
    try {
      const result: any = await inventoryService.linkPOToWRO(poNumber, link);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
      }
    } catch (error) {
      console.error("Failed to link PO to WRO:", error);
      setErrorMessage("Failed to link the PO to the WRO");
    }
  };

  // Same refresh as handleLinkWRO, for the allocation editor (which WRO lines each PO line draws on)
  const handleSetWroAllocations = async (poNumber: string, request: WroAllocationsRequest) => {
    try {
      const result: any = await inventoryService.setWroAllocations(poNumber, request);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
      }
    } catch (error) {
      console.error("Failed to save WRO allocations:", error);
      throw error;
    }
  };

  // WRO-centric write: every PO the WRO feeds comes back refreshed
  const handleSetWroLineAllocations = async (wroId: string, lines: WroLineAllocationsRequest["lines"]) => {
    try {
      const result: any = await inventoryService.setWroLineAllocations(wroId, lines);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
      }
    } catch (error) {
      console.error("Failed to save WRO allocations:", error);
      throw error;
    }
  };

  const handleWroCreated = (wro: { wroId: string; fulfillmentCenterName: string | null }) => {
    setSuccessMessage(
      `Created ShipBob receiving order ${wro.wroId}${wro.fulfillmentCenterName ? ` at ${wro.fulfillmentCenterName}` : ""} — it links to this PO automatically`
    );
    fetchPOsAndInventory();
  };

  const handleNetSuitePushed = (result: { netsuitePoId: string; tranId: string; url: string }) => {
    setSuccessMessage(`Created in NetSuite as ${result.tranId}`);
    fetchPOsAndInventory();
  };

  const handleCreateProduct = async (product: Parameters<InventoryService["createProduct"]>[0]) => {
    const created = await inventoryService.createProduct(product);
    if (!created) {
      setErrorMessage(`Failed to create ${product.sku} — the server or ShipBob rejected it`);
      return false;
    }
    // Make it orderable immediately, before the next ShipBob sync lands
    const option = { id: created.inventoryId, name: created.name, skus: [created.sku] };
    createdProductsRef.current = [...createdProductsRef.current, option];
    setInventoryOptions(prev => [...prev, option]);

    // The product exists in ShipBob either way — the cost write is what can fail,
    // and ShipBob's API cannot delete a product, so this must never read as
    // "creation failed" or the retry would duplicate it.
    if (!created.costRecorded) {
      setErrorMessage(
        `Created ${created.sku} in ShipBob (inventory ID ${created.inventoryId}), but its unit cost was NOT saved` +
        `${created.costError ? ` (${created.costError})` : ""}. Do not create it again — add its price in the SKU Master instead.`
      );
      return true;
    }

    // Make the new product's price pre-fill immediately, before the next fetch
    setCatalogSuggestions(prev => ({
      prices: { ...(prev?.prices ?? {}), [created.inventoryId]: { [product.priceType === "FOB" ? "fob" : "40hc"]: product.unitPrice } },
      manufacturerSkus: prev?.manufacturerSkus ?? {},
      tariffs: prev?.tariffs ?? { items: {}, suppliers: {}, recentByItem: {}, recentBySupplier: {} },
    }));
    setSuccessMessage(`Created ${created.sku} in ShipBob (inventory ID ${created.inventoryId}) — ready to order`);
    return true;
  };

  // POs missing what cashflow needs (mirrors the server's incomplete_po warning)
  const incompletePONumbers = useMemo(() =>
    new Set(namedPOs.filter(po => po.amount == null || po.supplierId == null).map(po => po.poNumber)),
  [namedPOs]);

  // ── PO management ──
  const pom = usePOManagement({
    inventoryOptions,
    suppliers,
    defaultNewPODate: moment().format("YYYY-MM-DD"),
    onSave: async (params) => {
      const posToSave = params.lineItems.map(li => ({
        inventoryId: li.inventoryId,
        poNumber: params.poNumber,
        arriveByDate: params.arriveByDate,
        quantity: li.quantity,
        description: params.description,
        placementDate: params.placementDate,
        etd: params.etd,
        amount: params.amount,
        supplierId: params.supplierId,
        tariffRate: li.tariffRate ?? null,
        status: params.status,
        brand: params.brand,
        countryOfDestination: params.countryOfDestination,
        paymentDateOverrides: params.paymentDateOverrides,
        unitPrice: li.unitPrice ?? null,
        priceType: params.priceType,
        containerType: params.containerType,
        shipToFcId: params.shipToFcId,
        manufacturerProductNumber: li.manufacturerProductNumber ?? null,
        skuConfirmed: Boolean(li.skuConfirmed ?? li.skuConfirmedAt),
      }));

      try {
        await persistPO(inventoryService, params, posToSave);
      } catch (error: any) {
        setErrorMessage(`PO ${params.poNumber} was not saved: ${error?.message || error}`);
        await fetchPOsAndInventory();
        return;
      }
      // Stay in the editor (Inno: saving must not close the PO), then offer
      // any prices that differ from the SKU master as new quotes.
      pom.markSaved();
      setSuccessMessage(`Saved PO ${params.poNumber}`);
      const changes = findPriceChanges(params.lineItems, params.containerType, catalogSuggestions);
      const productNumberChanges = findProductNumberChanges(params.lineItems, catalogSuggestions);
      if (changes.length > 0 || productNumberChanges.length > 0) setPriceChangesToOffer({ poNumber: params.poNumber, changes, productNumberChanges });
      await fetchPOsAndInventory();
    },
    onDelete: async (poToDelete) => {
      for (const li of poToDelete.lineItems) {
        const deleted = await inventoryService.deleteIncomingPO(li.inventoryId, poToDelete.poNumber);
        if (!deleted) {
          setErrorMessage(`Failed to delete PO ${poToDelete.poNumber}`);
          break;
        }
      }
      await fetchPOsAndInventory();
    },
  });

  // ── Row/bulk handlers ──
  const handleMarkReceived = async (poNumbers: string[]) => {
    const result = await inventoryService.markPOsReceived(poNumbers);
    if (!result) setErrorMessage("Failed to mark POs received");
    await fetchPOsAndInventory();
  };

  const handleUnmarkReceived = async (poNumbers: string[]) => {
    const result = await inventoryService.unmarkPOsReceived(poNumbers);
    if (!result) setErrorMessage("Failed to unmark POs received");
    await fetchPOsAndInventory();
  };

  const handlePromotePO = async (poNumber: string) => {
    const result = await inventoryService.promotePOToConfirmed(poNumber);
    if (!result) setErrorMessage(`Failed to promote PO ${poNumber}`);
    await fetchPOsAndInventory();
  };

  const handleTogglePaidTranche = async (poNumber: string, trancheOrder: number, paid: boolean) => {
    const po = consolidatedPOs.find(p => p.poNumber === poNumber);
    const updated = { ...(po?.paidTranches || {}), [String(trancheOrder)]: paid };
    setIsUpdatingPaidStatus(true);
    try {
      const result = await inventoryService.updatePaidTranches(poNumber, updated);
      if (!result) setErrorMessage(`Failed to update payment status for ${poNumber}`);
      await fetchPOsAndInventory();
    } finally {
      setIsUpdatingPaidStatus(false);
    }
  };

  const handleBulkMarkAllPaid = async (updates: Array<{ poNumber: string; paidTranches: Record<string, boolean> }>) => {
    setIsUpdatingPaidStatus(true);
    try {
      await Promise.all(updates.map(({ poNumber, paidTranches }) =>
        inventoryService.updatePaidTranches(poNumber, paidTranches)
      ));
      await fetchPOsAndInventory();
    } finally {
      setIsUpdatingPaidStatus(false);
    }
  };

  const handleBulkClearPaid = async (poNumbers: string[]) => {
    setIsUpdatingPaidStatus(true);
    try {
      await inventoryService.clearPaidTranches(poNumbers);
      await fetchPOsAndInventory();
    } finally {
      setIsUpdatingPaidStatus(false);
    }
  };

  const handleSuggestPoNumber = async () => {
    const suggestion = await inventoryService.getNextPoNumber(pom.editingPOCountryOfDestination);
    if (suggestion) {
      pom.dialogProps.onSetPONumber(suggestion.poNumber);
    } else {
      setErrorMessage("Failed to suggest a PO number");
    }
  };

  const handleGeneratePdf = async (poToPrint: PurchaseOrder) => {
    setGeneratingPdfPoNumber(poToPrint.poNumber);
    try {
      const blob = await inventoryService.downloadPoPdf(poToPrint.poNumber);
      if (blob) {
        downloadBlob(blob, `${poToPrint.poNumber.replace(/[^A-Za-z0-9._-]/g, "_")}.pdf`);
      } else {
        setErrorMessage(`Failed to generate the PDF for ${poToPrint.poNumber}`);
      }
    } finally {
      setGeneratingPdfPoNumber(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 3, width: "100%", minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h5">
            Purchase Orders
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color={shortfallsNeedingAttention > 0 ? "warning" : "inherit"}
              startIcon={<ReportProblemIcon />}
              onClick={() => setIsClaimsDialogOpen(true)}
            >
              Missing units{shortfallsNeedingAttention > 0 ? ` (${shortfallsNeedingAttention})` : ""}
            </Button>
            {/* Disabled until the catalog loads: the SKU builder's vocabulary and
                its duplicate check both come from it, so offering the dialog
                early invites a duplicate SKU. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              disabled={isLoading}
              onClick={() => setIsNewProductDialogOpen(true)}
            >
              New Product
            </Button>
          </Box>
        </Box>

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, py: 10 }}>
            <CircularProgress size={28} />
            <Typography color="text.secondary">Loading purchase orders…</Typography>
          </Box>
        ) : (
          <POManagementDialog
            {...pom.dialogProps}
            standalone
            consolidatedPOs={namedPOs}
            incompletePONumbers={incompletePONumbers}
            catalogSuggestions={catalogSuggestions}
            onSuggestPONumber={handleSuggestPoNumber}
            onGeneratePdf={handleGeneratePdf}
            generatingPdfPoNumber={generatingPdfPoNumber}
            wroOptions={wros}
            onLinkWRO={handleLinkWRO}
            onSetWroAllocations={handleSetWroAllocations}
            onSetWroLineAllocations={handleSetWroLineAllocations}
            onSuppliersChanged={setSuppliers}
            onWroCreated={handleWroCreated}
            onNetSuitePushed={handleNetSuitePushed}
            onPromotePO={(po) => handlePromotePO(po.poNumber)}
            onMarkReceived={(po) => handleMarkReceived([po.poNumber])}
            onUnmarkReceived={(po) => handleUnmarkReceived([po.poNumber])}
            onBulkMarkReceived={handleMarkReceived}
            onBulkUnmarkReceived={handleUnmarkReceived}
            onBulkMarkAllPaid={handleBulkMarkAllPaid}
            onBulkClearPaid={handleBulkClearPaid}
            onTogglePaidTranche={handleTogglePaidTranche}
            isUpdatingPaidStatus={isUpdatingPaidStatus}
          />
        )}

        <DeletePOConfirmation
          poToDelete={pom.poToDelete}
          isDeletingPO={pom.isDeletingPO}
          onConfirm={pom.confirmDeletePO}
          onCancel={pom.cancelDeletePO}
        />

        <NewProductDialog
          open={isNewProductDialogOpen}
          onClose={() => setIsNewProductDialogOpen(false)}
          existingSkus={existingSkus}
          onCreate={handleCreateProduct}
        />

        <SavePricesToSkuMasterDialog
          open={priceChangesToOffer != null}
          onClose={() => setPriceChangesToOffer(null)}
          poNumber={priceChangesToOffer?.poNumber ?? ""}
          changes={priceChangesToOffer?.changes ?? []}
          productNumberChanges={priceChangesToOffer?.productNumberChanges ?? []}
          onSaved={(count) => {
            setSuccessMessage(`Saved ${count} value${count === 1 ? "" : "s"} to the SKU master`);
            inventoryService.getCatalogPrices().then(result => { if (result) setCatalogSuggestions(result); });
          }}
        />

        <WroClaimsDialog
          open={isClaimsDialogOpen}
          onClose={() => {
            setIsClaimsDialogOpen(false);
            // Re-derive the badge from the server — claims filed, dismissed, and
            // resolved in the dialog all change what still needs attention
            fetchShortfallAttention();
          }}
          onResolved={(summary) => setSuccessMessage(summary)}
        />
      </Box>
    </LocalizationProvider>
  );
}
